// Akun (/account): the signed-in person's own profile, security, devices, sign-in history and preferences.

export const ACCOUNT = {
  title: 'Akun',
  sub: 'Profil, keamanan masuk, dan perangkat yang memakai akun Anda.',
  profile: 'Profil',
  profileLoading: 'Memuat profil',
  editName: 'Ubah nama',
  nameLabel: 'Nama',
  nameRequired: 'Nama wajib diisi.',
  nameTooLong: (max: number) => `Maksimal ${max} karakter.`,
  savingName: 'Menyimpan nama',
  save: 'Simpan',
  cancel: 'Batal',
  lastLogin: 'Masuk terakhir',
  created: 'Akun dibuat',
  region: (where: string) => `Wilayah ${where}`,
  preferences: 'Preferensi',
  sound: 'Bunyi peringatan kritis',
  soundHelp: 'Nada singkat setiap ada peringatan kritis baru. Berlaku di perangkat ini.',
  seeAll: (n: string) => `Lihat semua (${n})`,
  seeAllPlain: 'Lihat semua',
} as const;

/** Keamanan: the password row and the two-step verification row, with their dialogs. */
export const SECURITY = {
  title: 'Keamanan',
  password: 'Kata sandi',
  passwordChanged: (when: string) => `Terakhir diubah ${when}`,
  passwordNever: 'Belum pernah diubah',
  passwordChange: 'Ubah kata sandi',
  dialogTitle: 'Ubah kata sandi',
  dialogAdvice: 'Gunakan kata sandi yang kuat dan tidak dipakai di layanan lain.',
  dialogSignOut: 'Perangkat lain yang sedang masuk akan dikeluarkan.',
  dialogSubmit: 'Simpan kata sandi',
  changed: (n: number) => (n ? `Kata sandi diubah. ${n} perangkat lain dikeluarkan.` : 'Kata sandi diubah.'),
  totp: 'Verifikasi dua langkah',
  totpOn: 'Aktif',
  totpOff: 'Belum aktif',
  totpRequired: 'Wajib',
  totpSince: (when: string) => `Aktif sejak ${when}`,
  totpCodesLeft: (n: number) => (n ? `${n} kode pemulihan tersisa` : 'Kode pemulihan habis'),
  totpOffText: 'Lindungi akun dengan kode dari aplikasi autentikator selain kata sandi.',
  totpRequiredOff: 'Wajib untuk akun Anda. Aktifkan sekarang agar tetap dapat masuk.',
  totpLoadFailed: 'Status verifikasi dua langkah tidak dapat dimuat.',
  enable: 'Aktifkan',
  regenerate: 'Buat ulang kode pemulihan',
  disable: 'Nonaktifkan',
  enabled: (signedOut: number) => (signedOut ? `Verifikasi dua langkah diaktifkan. ${signedOut} perangkat lain dikeluarkan.` : 'Verifikasi dua langkah diaktifkan.'),
  disableTitle: 'Nonaktifkan verifikasi dua langkah?',
  disableText: 'Setelah dinonaktifkan, akun ini hanya dilindungi kata sandi. Masukkan kata sandi dan kode verifikasi untuk melanjutkan.',
  disableSubmit: 'Nonaktifkan',
  disabled: 'Verifikasi dua langkah dinonaktifkan',
  regenerateTitle: 'Buat ulang kode pemulihan?',
  regenerateText: 'Semua kode pemulihan lama langsung tidak berlaku. Masukkan kode verifikasi dari aplikasi untuk melanjutkan.',
  regenerateSubmit: 'Buat kode baru',
  regenerated: 'Kode pemulihan baru dibuat',
  codesLow: 'Kode pemulihan hampir habis. Buat ulang agar tetap dapat masuk bila ponsel hilang.',
} as const;

/** Perangkat aktif: the devices signed in to this account. */
export const DEVICES = {
  title: 'Perangkat aktif',
  loading: 'Memuat perangkat',
  noun: 'perangkat',
  thisDevice: 'Perangkat ini',
  unknownDevice: 'Perangkat tidak dikenal',
  signOut: 'Keluarkan',
  signOutLabel: (device: string) => `Keluarkan ${device}`,
  signOutOthers: 'Keluarkan semua perangkat lain',
  onlyThis: 'Hanya perangkat ini yang sedang masuk.',
  since: (rel: string) => `Masuk sejak ${rel}`,
  justNow: 'Masuk baru saja',
  drawerTitle: 'Semua perangkat aktif',
  confirmOneTitle: 'Keluarkan perangkat ini?',
  confirmOneBody: (device: string) => `${device} harus masuk lagi dengan kata sandi.`,
  confirmOthersTitle: 'Keluarkan semua perangkat lain?',
  confirmOthersBody: (n: number) => `${n} perangkat lain harus masuk lagi dengan kata sandi. Perangkat ini tetap masuk.`,
  confirmOthersLabel: 'Keluarkan semua',
  signedOutOne: 'Perangkat dikeluarkan',
  alreadyOut: 'Perangkat tersebut sudah keluar',
  signedOutOthers: (n: number) => (n ? `${n} perangkat lain dikeluarkan` : 'Tidak ada perangkat lain yang sedang masuk'),
} as const;

/** Riwayat masuk: the latest sign-in attempts of this account, failed ones included. */
export const SIGN_INS = {
  title: 'Riwayat masuk',
  loading: 'Memuat riwayat masuk',
  noun: 'percobaan masuk',
  empty: 'Belum ada riwayat masuk tercatat.',
  drawerTitle: 'Riwayat masuk',
  listLabel: 'Percobaan masuk',
  result: {
    success: 'Berhasil',
    wrong_password: 'Kata sandi salah',
    totp_invalid: 'Kode verifikasi salah',
    rate_limited: 'Diblokir sementara',
    inactive: 'Akun nonaktif',
    unknown_email: 'Email tidak dikenal',
    client_outdated: 'Aplikasi perlu dimuat ulang',
  } as Record<string, string>,
  failed: 'Gagal',
  withRecovery: 'dengan kode pemulihan',
} as const;
